class MacornerProductController {
  constructor(sectionRoot) {
    this.sectionRoot = sectionRoot;
    this.mediaShell = sectionRoot.querySelector('.mac-product-media-shell');
    this.customilyPreviewWrapper = sectionRoot.querySelector('[data-macorner-customily-preview-button]');
    this.customilyPreviewButton = this.customilyPreviewWrapper?.querySelector('.customily-preview-button');
    this.previewModal = document.getElementById('preview-container');
    this.accordionDetails = Array.from(sectionRoot.querySelectorAll('.mac-product-accordions details'));
    this.pendingCustomilyPreviewFrame = 0;

    if (!this.mediaShell) return;

    this.updateStickyOffset = this.updateStickyOffset.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.syncCustomilyPreviewState = this.syncCustomilyPreviewState.bind(this);
    this.scheduleCustomilyPreviewSync = this.scheduleCustomilyPreviewSync.bind(this);
    this.syncCustomilyPreviewButton = this.syncCustomilyPreviewButton.bind(this);
    this.handleCustomilyPreviewMutation = this.handleCustomilyPreviewMutation.bind(this);
    this.handleCustomilyOptionInteraction = this.handleCustomilyOptionInteraction.bind(this);
    this.handleMacornerCustomilyPreviewClick = this.handleMacornerCustomilyPreviewClick.bind(this);
    this.closeCustomilyPreviewModal = this.closeCustomilyPreviewModal.bind(this);

    this.accordionDetails.forEach((detailsElement) => {
      detailsElement.addEventListener('toggle', this.handleToggle);
    });

    this.customilyPreviewButton?.addEventListener('click', this.handleMacornerCustomilyPreviewClick);
    this.previewModal?.querySelector('#preview-close')?.addEventListener('click', this.closeCustomilyPreviewModal);
    this.previewModal?.querySelector('#preview-overlay')?.addEventListener('click', this.closeCustomilyPreviewModal);
    ['input', 'change', 'click', 'touchend'].forEach((eventName) => {
      this.sectionRoot.addEventListener(eventName, this.handleCustomilyOptionInteraction, true);
    });

    this.updateStickyOffset();
    this.syncCustomilyPreviewState();
    this.syncCustomilyPreviewButton();
    this.customilyPreviewObserver = new MutationObserver(this.handleCustomilyPreviewMutation);
    this.observeCustomilyPreview();
    this.customilyOptionsObserver = new MutationObserver(this.syncCustomilyPreviewButton);
    this.customilyOptionsObserver.observe(this.sectionRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    window.addEventListener('resize', this.updateStickyOffset, { passive: true });
  }

  observeCustomilyPreview() {
    this.customilyPreviewObserver.observe(this.mediaShell, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  handleCustomilyPreviewMutation() {
    this.customilyPreviewObserver.disconnect();

    try {
      this.syncCustomilyPreviewState();
    } finally {
      this.observeCustomilyPreview();
    }
  }

  scheduleCustomilyPreviewSync() {
    if (this.pendingCustomilyPreviewFrame) return;

    this.pendingCustomilyPreviewFrame = window.requestAnimationFrame(() => {
      this.pendingCustomilyPreviewFrame = 0;
      this.syncCustomilyPreviewState();
    });
  }

  handleCustomilyOptionInteraction(event) {
    const target = event.target;
    if (!target || !target.closest || !target.closest('#customily-options')) return;

    this.scheduleCustomilyPreviewSync();
    window.setTimeout(this.scheduleCustomilyPreviewSync, 50);
    window.setTimeout(this.scheduleCustomilyPreviewSync, 250);
  }

  getStickyHeaderOffset() {
    const stickyHeaderElement = document.querySelector('sticky-header');
    return stickyHeaderElement ? stickyHeaderElement.offsetHeight : 0;
  }

  updateStickyOffset() {
    this.sectionRoot.style.setProperty('--header-height', `${this.getStickyHeaderOffset()}px`);
  }

  syncCustomilyPreviewButton() {
    if (!this.customilyPreviewWrapper) return;

    const hasCustomilyOptions = !!(
      this.sectionRoot.querySelector('#customily-options') || document.querySelector('#customily-options')
    );

    this.customilyPreviewWrapper.hidden = !hasCustomilyOptions;
  }

  getCustomilyRenderedCanvas() {
    return (
      window.engraver?.canvas?.lowerCanvasEl ||
      this.sectionRoot.querySelector('.cl-canvas-container canvas') ||
      document.querySelector('.cl-canvas-container canvas')
    );
  }

  openCustomilyPreviewModal(previewImageUrl) {
    const previewModal = this.previewModal || document.getElementById('preview-container');
    const previewImage = previewModal?.querySelector('#preview-img');

    if (!previewModal || !previewImage || !previewImageUrl) return false;

    previewImage.src = previewImageUrl;
    previewModal.classList.add('macorner-preview-modal');
    previewModal.classList.remove('hidden');
    previewModal.style.display = 'block';
    previewModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    return true;
  }

  closeCustomilyPreviewModal() {
    if (!this.previewModal) return;

    this.previewModal.classList.add('hidden');
    this.previewModal.classList.remove('macorner-preview-modal');
    this.previewModal.style.display = 'none';
    this.previewModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  handleMacornerCustomilyPreviewClick(event) {
    event.preventDefault();

    const previewCanvas = this.getCustomilyRenderedCanvas();
    if (!previewCanvas) return;

    let previewImageUrl = '';
    try {
      previewImageUrl = previewCanvas.toDataURL('image/png');
    } catch (error) {
      console.warn('Macorner Customily preview could not be generated.', error);
      return;
    }

    this.openCustomilyPreviewModal(previewImageUrl);
  }

  removeInlineStyle(element, property) {
    if (!element || !element.style.getPropertyValue(property)) return;
    element.style.removeProperty(property);
  }

  cleanupCustomilyPreviewStyles(previewActive) {
    const canvasWrapper = this.mediaShell.querySelector('.canvas-wrapper');
    const canvasContainer = this.mediaShell.querySelector('.cl-canvas-container');
    const staleCanvasWrapperStyles = ['top', 'left', 'right', 'bottom', 'width', 'margin', 'z-index'];
    const staleCanvasContainerStyles = [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'width',
      'height',
      'max-width',
      'max-height',
      'margin',
      'z-index',
      'display',
      'justify-content',
      'align-items',
      'background-color',
    ];
    const staleMediaStyles = ['display', 'opacity', 'visibility', 'pointer-events'];

    if (previewActive && canvasWrapper) {
      canvasWrapper.style.removeProperty('position');
      staleCanvasWrapperStyles.forEach((property) => this.removeInlineStyle(canvasWrapper, property));
    }

    if (previewActive && canvasContainer) {
      staleCanvasContainerStyles.forEach((property) => this.removeInlineStyle(canvasContainer, property));
    }

    this.mediaShell.querySelectorAll('.product__media.media').forEach((mediaElement) => {
      staleMediaStyles.forEach((property) => this.removeInlineStyle(mediaElement, property));
    });

    this.mediaShell.querySelectorAll('.product__media-icon, .product__media-toggle').forEach((element) => {
      this.removeInlineStyle(element, 'display');
    });
  }

  syncCustomilyPreviewState() {
    const previewCanvas = this.mediaShell.querySelector('.cl-canvas-container');
    const canvasVisible =
      !!previewCanvas &&
      window.getComputedStyle(previewCanvas).display !== 'none' &&
      window.getComputedStyle(previewCanvas).visibility !== 'hidden';
    const canvasRendered =
      canvasVisible &&
      (previewCanvas.getBoundingClientRect().height > 0 ||
        !!previewCanvas.querySelector('.canvas-wrapper, canvas'));
    if (canvasRendered) {
      this.mediaShell.dataset.customilyPreviewShellHasRendered = 'true';
    } else if (!previewCanvas) {
      delete this.mediaShell.dataset.customilyPreviewShellHasRendered;
    }

    const previewActive =
      canvasRendered ||
      (this.mediaShell.dataset.customilyPreviewShellHasRendered === 'true' && !!previewCanvas);

    this.cleanupCustomilyPreviewStyles(previewActive);
    this.mediaShell.classList.toggle('mac-product-media-shell--customily-preview-active', previewActive);
  }

  handleToggle(event) {
    const detailsElement = event.currentTarget;

    if (!detailsElement.open) return;

    const summaryElement = detailsElement.querySelector('summary');
    const contentElement = detailsElement.querySelector('.accordion__content');

    if (!summaryElement || !contentElement) return;

    requestAnimationFrame(() => {
      const stickyOffset = this.getStickyHeaderOffset();
      const viewportPadding = window.innerWidth < 750 ? 14 : 24;
      const summaryRect = summaryElement.getBoundingClientRect();
      const contentRect = contentElement.getBoundingClientRect();
      const visibleBottom = window.innerHeight - viewportPadding;
      const shouldScroll =
        summaryRect.top < stickyOffset + viewportPadding || contentRect.bottom > visibleBottom;

      if (!shouldScroll) return;

      const targetTop = window.scrollY + summaryRect.top - stickyOffset - viewportPadding;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });
    });
  }
}

const initMacornerProducts = () => {
  // Random review count with localStorage persistence
  document.querySelectorAll('[data-random-review]').forEach((el) => {
    const productId = el.dataset.productId;
    const min = parseInt(el.dataset.min, 10);
    const max = parseInt(el.dataset.max, 10);
    const key = `mac_review_count_${productId}`;

    let count = localStorage.getItem(key);
    if (count === null) {
      count = Math.floor(Math.random() * (max - min + 1)) + min;
      localStorage.setItem(key, count);
    } else {
      count = parseInt(count, 10);
    }

    el.textContent = `${count.toLocaleString()} reviews`;
  });

  document.querySelectorAll('[data-macorner-product]').forEach((sectionRoot) => {
    if (sectionRoot.dataset.macornerProductInitialized === 'true') return;

    sectionRoot.dataset.macornerProductInitialized = 'true';
    new MacornerProductController(sectionRoot);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMacornerProducts, { once: true });
} else {
  initMacornerProducts();
}

document.addEventListener('shopify:section:load', initMacornerProducts);
